import React from 'react';
import Head from 'next/head';
import Image from 'next/image';
import { getSiteUrl } from '@utils/config';
import StArrowRightSvg from 'pubweb-smokey/dist/images/svg/iconography-16x16/st-arrow-right.svg';
import { ConflictMineralStyles } from './ConflictMinerals.styled';

const ConflictMinerals = () => {
  /* eslint-disable react/no-unescaped-entities */
  return (
    <ConflictMineralStyles>
      <Head htmlAttributes={{ lang: 'en' }}>
        <title>Conflict Minerals | Clayton Homes</title>
        <meta
          name="description"
          content="Clayton complies with the Dodd-Frank regulations regarding conflict minerals and all the reporting and transparency rules required by law."
        />
        <meta
          property="og:image"
          content={`${getSiteUrl(
            '/public-assets/images/conflict-minerals/conflict-minerals.png'
          )}`}
        />
      </Head>

      <div className="conflict-hero">
        <div className="constrained-width">
          <h1 className="large-heading hero-heading">Conflict Minerals</h1>
        </div>
      </div>

      <div className="overview constrained-width">
        <h2>Overview</h2>
        <p>
          What is the Dodd-Frank Act Section 1502 Conflict Minerals provision?
        </p>
      </div>

      <div className="basic-blurb constrained-width">
        <div className="basic-blurb-image">
          <div className="minerals-map">
            <Image
              src="/public-assets/images/conflict-minerals/conflict-minerals-map.svg"
              alt="Conflict minerals map"
              width={366}
              height={414}
            />
          </div>
        </div>
        <div className="basic-blurb-text">
          <p>
            Section 1502 of the Dodd-Frank Act is a provision related to
            sourcing conflict minerals, signed into law July 21, 2010. The
            intent of the provision is to deter — through increased transparency
            of companies’ sourcing practices — the extreme violence and human
            rights violations in the Democratic Republic of Congo (DRC) and
            neighboring countries funded by the exploitation and trade of
            certain minerals.
          </p>
          <p>
            Section 1502 instructs the U.S. Securities and Exchange Commission
            (SEC), in consultation with the U.S. Department of State, to
            promulgate regulations requiring certain companies to submit
            annually a description of measures taken to exercise due diligence
            on the source and chain of custody of Conflict Minerals. SEC issued
            final Conflict Minerals reporting rules on August 22, 2012. The goal
            is to have product and materials that are necessary to the
            production of a product and utilized in the manufacturing process of
            that product to be “conflict mineral free”.
          </p>
        </div>
      </div>

      <div className="about-conflict-minerals">
        <div className="basic-blurb basic-blurb-reverse constrained-width">
          <div className="basic-blurb-image type-container">
            <div className="conflict-mineral-types">
              <Image
                src="/public-assets/images/conflict-minerals/conflict-minerals.png"
                alt="Types of conflict minerals"
                width={383}
                height={625}
              />
            </div>
          </div>
          <div className="basic-blurb-text">
            <h3 className="what-they-are">What are Conflict Minerals?</h3>
            <p>
              Cassiterite (tin ore), wolframite (tungsten ore), coltan (tantalum
              ore) and gold. They often refer as 3TG (Tin, Tantalum, Tungsten
              and Gold), originating from the Democratic Republic of Congo or an
              adjoining country.
            </p>

            <h5>What is DRC conflict free?</h5>
            <p>
              "DRC conflict-free" is defined to mean the products that do not
              contain [conflict] minerals or their derivatives determined to be
              directly or indirectly financing or benefit armed groups from
              affected countries: Democratic Republic of the Congo (DRC);
              Central Africa Republic; The Republic of the Congo; Tanzania; The
              Republic of South Sudan; Zambia; Angola; Burundi; Rwanda and
              Uganda.
            </p>
          </div>
        </div>
      </div>

      <div className="basic-blurb constrained-width">
        <div className="basic-blurb-text-full-width">
          <h2>Clayton Conflict Minerals Policy Statement</h2>
          <p>
            CMH Manufacturing expects all vendors to follow the code of conduct
            as enumerated in the OECD Due Diligence Guidance for Responsible
            Supply Chain of Minerals from Conflict-Affected and High-Risk Areas.
            CMH Family of Brands has formulated a framework for detailed due
            diligence to further ensure their vendors meet the standards
            required for responsible global supply chain management of tin,
            tantalum, tungsten, their ores and mineral derivatives, and gold in
            conflict-affected and high-risk areas.
          </p>
          <p>
            CMH Manufacturing has incorporated measures to prevent future
            introduction of minerals that financially support conflict in these
            areas into its supply chain, while also verifying compliance from
            vendors in its current supply chain. The particular conflict regions
            currently referenced are the Democratic Republic of the Congo and
            the outlying countries, termed “covered countries”. CMH Family of
            Brands will support and actively contribute to industry initiatives
            in regards to conflict materials and the advocacy of ethical
            business practices.
          </p>
          <p>
            CMH Manufacturing will not tolerate products in its supply chain
            that contain minerals that directly or indirectly finance or benefit
            armed groups.
          </p>
          <p>
            CMH Manufacturing and its vendors will not knowingly contribute to
            conflict through the purchase or trade of products containing
            minerals from the conflict region. Vendors are expected to adhere to
            the EICC Code of Conduct, established to ensure worker safety and
            fairness, environmental responsibility, and business efficiency, and
            deal exclusively with known responsible sources.
          </p>
          <p>
            CMH Manufacturing expects all vendors to similarly adopt a standard
            of business ethic and process of due diligence to ensure their
            vendors are not associated with armed groups. All necessary
            declarations, with proper utilization of the EICC-GeSI Conflict
            Minerals Reporting Template, will be required from vendors. Origin
            of minerals must be known and documented in these reports to ensure
            observance of this standard. Vendors are expected to use this method
            as well as observing and cooperating with industry initiatives
            designed to eliminate the use of conflict minerals.
          </p>
          <p>
            CMH Manufacturing’s commitment to the protection of human rights and
            responsible business practices is to remain a constant guideline for
            trade worldwide.
          </p>

          <hr />
        </div>
      </div>

      <div className="basic-blurb constrained-width">
        <div className="basic-blurb-text">
          <h2>Helpful resources</h2>
          <p>
            For questions or concerns related to our conflict mineral
            initiatives, suppliers and other external stakeholders may contact
            us at{' '}
            <a
              href="mailto:conflictminerals@claytonhomes.com"
              target="_blank"
              rel="noopener noreferrer"
              className="email-conflict-minerals"
            >
              conflictminerals@claytonhomes.com
            </a>{' '}
            or by mail at Berkshire Hathaway Inc., 3555 Farnam Street, Suite
            1400, Omaha, NE 68131.
          </p>
        </div>
        <div className="basic-blurb-text resources">
          <h5 className="learn-more">More information on conflict minerals:</h5>
          <p>
            <a
              href="https://www.responsiblemineralsinitiative.org/"
              target="_blank"
              rel="noopener noreferrer"
              className="responsible-minerals-link"
            >
              The Responsible Minerals Initiative{' '}
              <StArrowRightSvg alt="Arrow Right" />
            </a>
          </p>
          <p>
            <a
              href="https://www.eiccoalition.org/initiatives/conflict-free-sourcing-initiative"
              target="_blank"
              rel="noopener noreferrer"
              className="eicc-link"
            >
              EICC's conflict mineral site <StArrowRightSvg alt="Arrow Right" />
            </a>
          </p>
        </div>
      </div>
    </ConflictMineralStyles>
  );
  /* eslint-enable react/no-unescaped-entities */
};

export default ConflictMinerals;
